import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Small stale-while-revalidate request hook for values that are cheap to keep
 * around between screens (badge counts, small lists).
 *
 * Results live in a module-level cache keyed by `key`. A cache hit renders on
 * the very first frame with `loading=false` while a refetch runs in the
 * background; a miss renders `loading=true` with `data=null` until the first
 * response arrives. A refetch that returns identical data does not touch state,
 * so nothing re-renders or replays entrance animations. A failed refetch keeps
 * the cached value; a failure with nothing cached falls back to `fallback`
 * (when given) so the UI never spins forever on a non-200.
 *
 * `fetcher` is read through a ref, so callers may pass an inline function
 * without re-triggering the request; the request re-runs when `key` or
 * `enabled` change, or when `reload()` is called.
 *
 * Same request-id + mounted guard as useReadyToGoContent: each hook instance
 * has its own counter, so two hooks can never cancel each other's requests.
 *
 * @returns {{ data: any, loading: boolean, version: string, reload: () => void }}
 */

const cache = new Map()

const isSameData = (a, b) => {
    if (a === b) return true
    try {
        return JSON.stringify(a) === JSON.stringify(b)
    } catch (err) {
        return false
    }
}

const readCache = (key) => (cache.has(key) ? cache.get(key) : null)

/** Test/debug helper: drop everything cached. Not used by the UI. */
export const clearCachedRequests = () => cache.clear()

export default function useCachedRequest(key, fetcher, { enabled = true, fallback } = {}) {
    const [snapshot, setSnapshot] = useState(() => ({ key, data: readCache(key), arrivals: 0 }))
    const [reloadCount, setReloadCount] = useState(0)
    const requestRef = useRef(0)
    const mountedRef = useRef(true)
    const fetcherRef = useRef(fetcher)
    fetcherRef.current = fetcher

    useEffect(() => {
        mountedRef.current = true
        return () => {
            mountedRef.current = false
        }
    }, [])

    useEffect(() => {
        if (!enabled) return
        const requestId = ++requestRef.current
        const isCurrent = () => mountedRef.current && requestId === requestRef.current

        Promise.resolve()
            .then(() => fetcherRef.current())
            .then((data) => {
                if (!isCurrent()) return
                if (cache.has(key) && isSameData(cache.get(key), data)) return
                cache.set(key, data)
                setSnapshot((prev) => ({ key, data, arrivals: prev.key === key ? prev.arrivals + 1 : 1 }))
            })
            .catch((err) => {
                if (!isCurrent()) return
                console.error(`useCachedRequest: "${key}" failed`, err)
                if (!cache.has(key) && fallback !== undefined) setSnapshot({ key, data: fallback, arrivals: 1 })
            })
        // `fallback` is a plain value; reading it once is enough.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, enabled, reloadCount])

    const reload = useCallback(() => setReloadCount((n) => n + 1), [])

    // Read synchronously so a key switch renders cached data on the first frame
    // instead of flashing the previous key's value.
    const current = snapshot.key === key ? snapshot : { key, data: readCache(key), arrivals: 0 }
    const loading = enabled && current.data === null

    return { data: current.data, loading, version: `${key}:${current.arrivals}`, reload }
}
